import { useRouter as useNextRouter } from "next/router";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isCaseRoute } from "@medbillai/utils/urls";
import { analyticsService, type PanelSource, type PanelToggleAction } from "../../lib/analytics";
import type { CaseListFilters } from "../../screens/cases/utils/types";
export const CASES_PANEL_WIDTH = 380;
type CasesPanelContextInterface = CaseListFilters & {
  isOpen: boolean;
  togglePanel: (source: PanelSource, open?: boolean) => void;
  markKeepOpen: () => void;
  isOnCasesPage: boolean;
  setFilters: (filters: CaseListFilters) => void;
};
const initialContext: CasesPanelContextInterface = {
  isOpen: false,
  togglePanel: () => {
    throw new Error("CasesPanelContext not initialized");
  },
  markKeepOpen: () => {
    throw new Error("CasesPanelContext not initialized");
  },
  isOnCasesPage: false,
  showRead: true,
  showUnread: true,
  showClosed: false,
  setFilters: () => {
    throw new Error("CasesPanelContext not initialized");
  }
};
const trackPanelToggle = (action: PanelToggleAction, source: PanelSource) => {
  analyticsService.trackEvent("CasesPanelToggled", {
    action,
    source
  });
};
const CasesPanelContext = createContext<CasesPanelContextInterface>(initialContext);
export type CasesPanelProviderProps = {
  children: React.ReactNode;
};
export function CasesPanelProvider({
  children
}: CasesPanelProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showRead, setShowRead] = useState(true);
  const [showUnread, setShowUnread] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const router = useNextRouter();
  const keepOpenRef = useRef(false);
  useEffect(() => {
    const handleRouteChange = (newUrl: string) => {
      if (keepOpenRef.current && isCaseRoute(newUrl)) {
        keepOpenRef.current = false;
        return; // We keep the panel open if the user clicked a case list item
      }
      keepOpenRef.current = false;
      setIsOpen(false);
    };
    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events]);
  const togglePanel = useCallback((source: PanelSource, open?: boolean) => {
    setIsOpen(prev => {
      const next = open ?? !prev;
      if (next !== prev) {
        trackPanelToggle(next ? "open" : "close", source);
      }
      return next;
    });
  }, []);
  const markKeepOpen = useCallback(() => {
    keepOpenRef.current = true;
  }, []);
  const setFilters = useCallback((filters: CaseListFilters) => {
    setShowRead(filters.showRead);
    setShowUnread(filters.showUnread);
    setShowClosed(filters.showClosed);
  }, []);
  const isOnCasesPage = isCaseRoute(router.pathname);
  const contextValue = useMemo<CasesPanelContextInterface>(() => ({
    isOpen,
    togglePanel,
    markKeepOpen,
    isOnCasesPage,
    showRead,
    showUnread,
    showClosed,
    setFilters
  }), [isOpen, togglePanel, markKeepOpen, isOnCasesPage, showRead, showUnread, showClosed, setFilters]);
  return <CasesPanelContext.Provider value={contextValue} data-sentry-element="CasesPanelContext.Provider" data-sentry-component="CasesPanelProvider" data-sentry-source-file="CasesPanelProvider.tsx">
      {children}
    </CasesPanelContext.Provider>;
}
export const useCasesPanel = (): CasesPanelContextInterface => {
  const context = useContext(CasesPanelContext);
  if (!context) {
    throw new Error("useCasesPanel must be used within CasesPanelProvider");
  }
  return context;
};